import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import { isValidWordFull, loadDictionary } from '../../shared/dictionary';
import { findShortestPath, isOneLetterApart, type WordLadderLevel } from './types';
import './wordLadder.css';

export default function WordLadderGame() {
  const [data, setData] = useState<{
    level: WordLadderLevel;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<WordLadderLevel>('wordLadder')
      .then((res) => {
        if (cancelled) return;
        if (!res) setLoadError('No daily puzzle published for Word Ladder yet.');
        else setData({ level: res.data, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="wordLadder" />;
  if (!data) {
    return (
      <GameShell gameId="wordLadder">
        <LoadingScreen label="Fetching today's ladder…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('wordLadder', data.dateKey)) {
    return (
      <FinishedDaily gameId="wordLadder" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <WordLadderPlay
      key={data.dateKey}
      level={data.level}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

function WordLadderPlay({
  level,
  dateKey,
  isToday,
}: {
  level: WordLadderLevel;
  dateKey: string;
  isToday: boolean;
}) {
  const startWord = level.startWord.toUpperCase();
  const endWord = level.endWord.toUpperCase();
  const wordLength = level.wordLength ?? startWord.length;
  const optimalSteps = level.steps ?? 0;

  const { restored, save } = useDailySnapshot<{ chain: string[]; hintUsed: boolean }>(
    'wordLadder',
    dateKey,
  );

  const [chain, setChain] = useState<string[]>(() => restored?.chain ?? []);
  const [input, setInput] = useState<(string | null)[]>(() => startWord.split(''));
  const [cursor, setCursor] = useState<number | null>(0);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [hintBusy, setHintBusy] = useState(false);
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(save, { chain, hintUsed }, !completed);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (completed) recordOutcome('wordLadder', dateKey, 'won', { subtitle: `Solved in ${chain.length - 1} steps.` });
  }, [completed, dateKey]);

  const previousWord = chain.length > 0 ? chain[chain.length - 1] : startWord;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chain.length]);

  const showError = (msg: string) => {
    setError(msg);
    setTimeout(() => setError(null), 1600);
  };

  const useHint = useCallback(async () => {
    if (completed || hintBusy || hintUsed) return;
    setHintBusy(true);
    try {
      // The hint's BFS deliberately walks the common list: a shortest-path
      // search over all 125k entries is far too slow, and iOS notes the same
      // constraint. Submissions are still checked against the full list.
      const dict = await loadDictionary();
      const path = findShortestPath(previousWord, endWord, dict);
      if (!path || path.length < 2) {
        showError('No hint available');
        return;
      }
      const next = path[1];
      setHintUsed(true);
      setChain((prev) => [...prev, next]);
      if (next === endWord) {
        setCompleted(true);
      } else {
        setInput(next.split(''));
        setCursor(0);
      }
    } finally {
      setHintBusy(false);
    }
  }, [completed, hintBusy, hintUsed, previousWord, endWord]);

  const submit = useCallback(async () => {
    const letters = input.map((c) => c ?? '');
    if (letters.some((c) => !c)) {
      showError('Fill in all letters');
      return;
    }
    const word = letters.join('').toUpperCase();
    if (!isOneLetterApart(previousWord, word)) {
      showError('Change exactly one letter');
      return;
    }
    const valid = await isValidWordFull(word);
    if (!valid) {
      showError('Not a valid word');
      return;
    }
    setChain((prev) => [...prev, word]);
    if (word === endWord) {
      setCompleted(true);
    } else {
      setInput(word.split(''));
      setCursor(0);
    }
  }, [input, previousWord, endWord]);

  const inputLetter = useCallback(
    (letter: string) => {
      if (completed || cursor == null) return;
      setInput((prev) => {
        const next = [...prev];
        next[cursor] = letter.toUpperCase();
        return next;
      });
      const nextIdx = cursor + 1;
      if (nextIdx < wordLength) setCursor(nextIdx);
      else setCursor(null);
    },
    [cursor, wordLength, completed],
  );

  const deleteLetter = useCallback(() => {
    if (completed) return;
    if (cursor != null && input[cursor] != null) {
      setInput((prev) => {
        const next = [...prev];
        next[cursor] = null;
        return next;
      });
      return;
    }
    if (cursor != null && cursor > 0) {
      setCursor(cursor - 1);
      setInput((prev) => {
        const next = [...prev];
        next[cursor - 1] = null;
        return next;
      });
      return;
    }
    for (let i = input.length - 1; i >= 0; i--) {
      if (input[i] != null) {
        setInput((prev) => {
          const next = [...prev];
          next[i] = null;
          return next;
        });
        setCursor(i);
        return;
      }
    }
  }, [cursor, input, completed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
        inputLetter(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        deleteLetter();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inputLetter, deleteLetter, submit]);

  return (
    <GameShell
      gameId="wordLadder"
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${startWord} → ${endWord}`}
      actions={
        <button
          className="btn btn-secondary"
          onClick={useHint}
          disabled={completed || hintBusy || hintUsed}
          title="Reveal the next step (one per puzzle)"
        >
          💡 Hint
        </button>
      }
    >
      <div className="wl-chain">
        <WordRow word={startWord} style="start" label="START" />
        <ChainArrow />

        {chain.map((w, i) => (
          <Fragment key={i}>
            <WordRow word={w} style="submitted" />
            <ChainArrow />
          </Fragment>
        ))}

        {!completed && (
          <>
            <EditableRow
              input={input}
              cursor={cursor}
              onSelect={setCursor}
              shaking={!!error}
            />
            <ChainArrow />
          </>
        )}

        <WordRow word={endWord} style="target" label="TARGET" />
        <div ref={bottomRef} />
      </div>

      {error && <div className="wl-toast">{error}</div>}

      {!completed && (
        <div className="wl-bottom">
          <button className="btn btn-primary wl-check" onClick={submit}>
            ✓ CHECK
          </button>
          <Keyboard onLetter={inputLetter} onBack={deleteLetter} />
        </div>
      )}

      {completed && (
        <DailyDoneOverlay
          dateKey={dateKey}
          isToday={isToday}
          subtitle={`Solved in ${chain.length} ${chain.length === 1 ? 'step' : 'steps'}${
            optimalSteps ? ` (optimal: ${optimalSteps})` : ''
          }.`}
        />
      )}
    </GameShell>
  );
}

function WordRow({
  word,
  style,
  label,
}: {
  word: string;
  style: 'start' | 'submitted' | 'target';
  label?: string;
}) {
  return (
    <div className={`wl-row wl-row-${style}`}>
      {label && <div className="wl-row-label">{label}</div>}
      <div className="wl-tiles">
        {word.split('').map((ch, i) => (
          <div key={i} className={`wl-tile wl-tile-${style}`}>
            {ch}
          </div>
        ))}
      </div>
    </div>
  );
}

function EditableRow({
  input,
  cursor,
  onSelect,
  shaking,
}: {
  input: (string | null)[];
  cursor: number | null;
  onSelect: (i: number) => void;
  shaking: boolean;
}) {
  return (
    <div className={'wl-row wl-row-current' + (shaking ? ' is-shake' : '')}>
      <div className="wl-tiles">
        {input.map((ch, i) => (
          <button
            key={i}
            className={'wl-tile wl-tile-editable' + (cursor === i ? ' is-selected' : '')}
            onClick={() => onSelect(i)}
          >
            {ch ?? ''}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChainArrow() {
  return <div className="wl-arrow">▼</div>;
}

function Keyboard({ onLetter, onBack }: { onLetter: (l: string) => void; onBack: () => void }) {
  const rows = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];
  return (
    <div className="wl-keyboard">
      {rows.map((row, i) => (
        <div key={i} className="wl-keyboard-row">
          {row.split('').map((letter) => (
            <button key={letter} className="wl-key" onClick={() => onLetter(letter)}>
              {letter}
            </button>
          ))}
          {i === rows.length - 1 && (
            <button className="wl-key wl-key-back" onClick={onBack}>
              ⌫
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
